/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useState, useCallback, useRef } from 'react';
import axiosCall from '../../services/axios';
import Styles from '../../css/AddStopCodeForm.module.css';
import BtnStyles from '../../css/AutopayPage.module.css';
import useAbortController from '../../common/hooks/useAbortController';
import useStopCodes from '../../common/hooks/useStopCodes';
import LoadingModal from '../modals/LoadingModal';
import ButtonBorder from '../ButtonBorder';
import { Button } from '@mui/material';
import { useLoanContext } from '../../contexts/LoanContext';
import { useAuthContext } from '../../contexts/AuthContext';
import { useViewingContext } from '../../contexts/ViewingContext';
import EnvelopeWithBox from '../EnvelopeWithBox';
import ButtonBorderWrapper from '../ButtonBorderWrapper';
import { preventDefaultAndStopPropagation } from '../../common/UtilityFunctions';

const AddStopCode = () => {
  const { qidData, qidLoaded } = useStopCodes();
  const [errorMsg, setErrorMsg] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [submitProcessing, setSubmitProcessing] = useState(false);
  const batchResponseData = useRef(null);
  const stopCode = useRef(null);
  const loanNumbersRaw = useRef('');
  const loanNumbers = useRef([]);
  const errorBuilder = useRef('');
  const successBuilder = useRef('');
  const numberAdded = useRef(0);
  const stopCodeInput = useRef(null);
  const loanNumberInput = useRef(null);
  const submitButton = useRef(null);
  const { get } = useAbortController();
  const { token, userId, fullAccess } = useAuthContext();
  const { isServicerView } = useViewingContext();
  const { refetchLoanData } = useLoanContext();

  const loansPlaceholder = '000000000000\n000000000000\n000000000000\n000000000000\n000000000000';

  const isNumeric = useCallback((str) => {
    return !/[^0-9]/.test(str);
  }, []);

  const checkStopCode = useCallback(() => {
    // undefined check accounts for when stop codes do not load
    if (stopCode.current !== 'DEFAULT' && stopCode.current !== undefined) return true;
    errorBuilder.current += 'You must select a stop code.\n\n';
    return false;
  }, [stopCode]);

  const checkUID = useCallback(() => {
    if (userId === null) {
      errorBuilder.current += 'You must be logged in, no UID found in session.\n\n';
      return false;
    } else if (userId.length !== 4) {
      errorBuilder.current += 'Your UID must be 4 characters.\n\n';
      return false;
    }
    return true;
  }, [userId]);

  const checkLoanNumbers = useCallback(() => {
    if (loanNumbersRaw.current !== '') {
      loanNumbers.current = loanNumbersRaw.current.replace("/, -'/g", '').split('\n');

      for (let i = 0; i < loanNumbers.current.length; i++) {
        if (loanNumbers.current[i].length !== 12) {
          errorBuilder.current += 'Loan ' + loanNumbers.current[i] + ' is the wrong length, should be 12 digits.\n\n';
          return false;
        }
        if (!isNumeric(loanNumbers.current[i])) {
          errorBuilder.current += 'Loan ' + loanNumbers.current[i] + ' contains non-numeric characters.\n\n';
          return false;
        }
      }
      return true;
    } else {
      errorBuilder.current += 'Loan number(s) input can not be empty.\n\n';
      return false;
    }
  }, [isNumeric, loanNumbers, loanNumbersRaw]);

  // retrieve data for user provided loan(s)
  const getLoanBatchInfo = useCallback(async () => {
    if (!token) return null;
    let response = null;
    let batchRequestBody = {};
    loanNumbers.current.forEach((loan) => {
      batchRequestBody[loan] = { endpoint: `loan/` + loan };
    });
    const config = get();
    const headers = {
      'Session-Token': token,
    };
    try {
      response = await axiosCall({ url: 'batch', method: 'post', data: batchRequestBody }, config, headers);
    } catch (e) {
      console.log(e);
      errorBuilder.current += 'Batch Loan Endpoint Error: ' + e + '.\n\n';
    }
    return response;
  }, [get, loanNumbers, token]);

  // add a stop code to a loan
  const addStopCode = useCallback(
    async (index, loanNo) => {
      if (!token || index == null || !loanNo) return false;
      const config = get();
      const headers = {
        'Session-Token': token,
      };
      try {
        console.log(
          await axiosCall(
            {
              url: `live/loan/${loanNo}`,
              method: 'POST',
              data: {
                servicingStopCodes: [
                  {
                    $index: index,
                    servStopCode: stopCode.current,
                    servStopUid: userId,
                  },
                ],
              },
            },
            config,
            headers,
          ),
        );
        refetchLoanData();
        return true;
      } catch (e) {
        console.log(e);
        errorBuilder.current += 'Loan ' + loanNo + ' Error: ' + e + '.\n\n';
        return false;
      }
    },
    [stopCode, userId, get, token],
  );

  // for a single loan, determine the following:
  //      if it exists
  //      what index to add the stop code to
  //      if the stop code is already applied
  // then, if able, add the stop code to the loan
  const checkLoan = useCallback(
    async (loanNumber) => {
      // existing loan determination
      if (batchResponseData.current[loanNumber]?.hasOwnProperty('error')) {
        errorBuilder.current +=
          'Loan ' + loanNumber + ' error: ' + batchResponseData.current[loanNumber]?.error + '.\n\n';
      } else {
        // stop code index determination
        let index = 0;
        let validIndex = false;
        if (batchResponseData.current[loanNumber]?.data?.servicingStopCodes?.length === 0) {
          index = 0;
          validIndex = true;
        } else if (batchResponseData.current[loanNumber]?.data?.servicingStopCodes?.length >= 6) {
          errorBuilder.current += 'Loan ' + loanNumber + ' already has max amount of stop codes.\n\n';
          validIndex = false;
        } else {
          index = batchResponseData.current[loanNumber]?.data?.servicingStopCodes?.length;
          validIndex = true;
        }

        // ensure duplicate stop codes are not added and account for possible index gap
        // originally was using the following for loop boundary: batchResponseData.current[loanNumber]?.data?.servicingStopCodes?.length
        // however, if a stop code index gap is present such that codes exist with indexes 1-5 and not 0, length would be 5
        // and index 4 would be the last checked for duplicates, meaning a duplicate of the last code could be improperly added at index 0
        for (let j = 0; validIndex && j < 6; j++) {
          let comparisonCode = batchResponseData.current[loanNumber]?.data?.servicingStopCodes[j]?.servStopCode;
          let comparisonIndex = batchResponseData.current[loanNumber]?.data?.servicingStopCodes[j]?.$index;
          if (comparisonCode !== undefined && comparisonCode === stopCode.current) {
            errorBuilder.current +=
              'Loan ' + loanNumber + ' already has stop code ' + stopCode.current + ' applied.\n\n';
            validIndex = false;
          } else if (comparisonIndex !== undefined && comparisonIndex !== j) {
            index = j;
          }
        }

        // add the stop code
        if (validIndex) {
          if (addStopCode(index, loanNumber)) {
            numberAdded.current += 1;
            successBuilder.current += loanNumber + '\n\n';
          }
        }
      }
    },
    [addStopCode],
  );

  // process form input
  const formSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      submitButton.current.disabled = true;
      setSubmitProcessing(true);
      try {
        // reset user messages
        errorBuilder.current = '';
        successBuilder.current = '';
        numberAdded.current = 0;
        setErrorMsg('');
        setSuccessMsg('');

        // grab form input
        stopCode.current = stopCodeInput.current?.value;
        loanNumbersRaw.current = loanNumberInput.current?.value;

        // input/value validation
        const okStopCode = checkStopCode();
        const okUID = checkUID();
        const okLoanNumbers = checkLoanNumbers();

        if (okStopCode && okUID && okLoanNumbers) {
          batchResponseData.current = await getLoanBatchInfo();

          // on successful batch retrieval
          if (batchResponseData.current !== null) {
            // asynchronously process each loan from the batch response
            const promises = loanNumbers.current.map(async (loan) => {
              return checkLoan(loan);
            });
            await Promise.all(promises);
            // clear form inputs and selections
            stopCodeInput.current.value = 'DEFAULT';
            loanNumberInput.current.value = '';
            // add header to success message based on number of successfully added stop codes
            let successHeader = '';
            if (numberAdded.current > 1) {
              successHeader =
                'Stop code ' +
                stopCode.current +
                ' has been added to the following ' +
                numberAdded.current +
                ' loans:\n\n';
            } else if (numberAdded.current === 1) {
              successHeader = 'Stop code ' + stopCode.current + ' has been added to the following loan:\n\n';
            }
            if (successHeader !== '') successBuilder.current = successHeader + successBuilder.current;
          } else {
            errorBuilder.current += 'Loan batch retrieval failed.\nNo stop codes were added.\n';
          }
        }
        setErrorMsg(errorBuilder.current);
        setSuccessMsg(successBuilder.current);
      } catch (e) {
        console.log(e);
        setErrorMsg(e);
      }
      setSubmitProcessing(false);
      submitButton.current.disabled = false;
    },
    [checkStopCode, checkUID, checkLoanNumbers, getLoanBatchInfo, checkLoan],
  );

  return (
    <ButtonBorderWrapper>
      <EnvelopeWithBox blockName='Add Stop Code' className={Styles.smallEnvelope}>
        {(submitProcessing || qidLoaded === 0) && <LoadingModal open={submitProcessing || qidLoaded === 0} />}
        <form onSubmit={preventDefaultAndStopPropagation}>
          <h4 className={Styles.formHeader}>Please select a stop code and enter the loans it should apply to:</h4>
          {qidLoaded === 2 ? (
            <p className={Styles.errorMsg}>Unable to retrieve stop code options.</p>
          ) : (
            <label className={Styles.formLabel}>
              <span>Stop Code:</span>
              <select ref={stopCodeInput} className={Styles.formInput} name='stopCode'>
                <option value='DEFAULT'>-- Select an Option --</option>
                {qidData.map((currentOption, i) => (
                  <option key={i} value={currentOption.code}>
                    {currentOption.code} - {currentOption.description}
                  </option>
                ))}
              </select>
            </label>
          )}
          <br />
          <label className={Styles.formLabel}>
            <span>Loan(s):</span>
            <textarea
              ref={loanNumberInput}
              name='loanNumber'
              placeholder={loansPlaceholder}
              className={Styles.formInput}
              style={{ height: '200px' }}
            />
          </label>
          <p className={Styles.loanFormat}>Separated by the ENTER key.</p>
          <br />
          {errorMsg ? <p className={Styles.errorMsg}>{errorMsg}</p> : <p className={Styles.hidden}></p>}
          {successMsg ? <p className={Styles.successMsg}>{successMsg}</p> : <p className={Styles.hidden}></p>}
        </form>
      </EnvelopeWithBox>
      <ButtonBorder>
        <Button type='button' disabled={!fullAccess && isServicerView} onClick={formSubmit} ref={submitButton} className={BtnStyles.button}>
          Submit
        </Button>
      </ButtonBorder>
    </ButtonBorderWrapper>
  );
};

export default AddStopCode;
